import React, { useEffect, useState } from 'react';
import { motion, useAnimation } from 'framer-motion';
import { Github, Linkedin } from 'lucide-react';
import { profileData } from '../utils/mockData';
import GalaxyBackground from './GalaxyBackground';

const Hero = () => {
  const controls = useAnimation();
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const sequence = async () => {
      await controls.start('visible');
      setIsLoaded(true);
    };
    sequence();
  }, [controls]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.3,
      },
    },
  };

  const lineVariants = {
    hidden: { opacity: 0, y: 20, clipPath: 'inset(0 0 100% 0)' },
    visible: {
      opacity: 1,
      y: 0,
      clipPath: 'inset(0 0 0% 0)',
      transition: {
        duration: 0.8,
        ease: [0.645, 0.045, 0.355, 1],
      },
    },
  };

  const subtextVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut',
      },
    },
  };

  return (
    <section className="hero-section">
      {/* 3D Galaxy Background (full-bleed) */}
      <GalaxyBackground />
      <div className="hero-readability-overlay" />
      <div className="hero-container">
        {/* Animated Background Elements */}
        <div className="hero-bg-grid" />
        <motion.div
          className="hero-bg-gradient"
          animate={{
            scale: [1, 1.1, 1],
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />

        {/* Hero Content */}
        <motion.div
          className="hero-content"
          variants={containerVariants}
          initial="hidden"
          animate={controls}
        >
          <motion.div variants={subtextVariants} className="hero-badge">
            <span className="badge-headline">I make complex infra boring</span>
            <span className="badge-subline">
              Platform Engineering · Cloud · OpenShift · Automation
            </span>
          </motion.div>

          {/* Main Title with Line-by-Line Reveal */}
          <div className="hero-title-container">
            <motion.h1 variants={lineVariants} className="hero-title-line">
              {profileData.name}
            </motion.h1>
            <motion.h2 variants={lineVariants} className="hero-subtitle-line">
              {profileData.title}
            </motion.h2>
          </div>

          {/* Manifesto Lines */}
          <motion.div variants={containerVariants} className="hero-manifesto">
            <motion.p variants={lineVariants} className="manifesto-line">
              {profileData.subtitle}
            </motion.p>
            <motion.p variants={subtextVariants} className="manifesto-description">
              {profileData.description}
            </motion.p>
          </motion.div>

          {/* CTA Buttons */}
          <motion.div
            variants={subtextVariants}
            className="hero-cta-container"
          >
            <motion.a
              href={profileData.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              className="hero-cta-primary"
              whileHover={{ scale: 1.02, y: -2 }}
              whileTap={{ scale: 0.98 }}
              transition={{ type: 'spring', stiffness: 400, damping: 17 }}
            >
              <Linkedin size={18} />
              LinkedIn
            </motion.a>
            <motion.a
              href={profileData.github}
              target="_blank"
              rel="noopener noreferrer"
              className="hero-cta-secondary"
              whileHover={{ scale: 1.02, x: 2 }}
              whileTap={{ scale: 0.98 }}
              transition={{ type: 'spring', stiffness: 400, damping: 17 }}
            >
              <Github size={18} />
              GitHub
            </motion.a>
          </motion.div>
        </motion.div>

        {/* Scroll Indicator */}
        <motion.div
          className="scroll-indicator"
          initial={{ opacity: 0 }}
          animate={{ opacity: isLoaded ? 1 : 0 }}
          transition={{ delay: 2, duration: 0.6 }}
        >
          <motion.div
            className="scroll-line"
            animate={{ y: [0, 12, 0] }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          />
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
